import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Workflow, Eye, FileDown, Loader2 } from 'lucide-react';
import Button from './Buttons';
import RiskWorkflowActions from './RiskWorkflowActions';
import { useEvidenceDownload } from '../hooks/useEvidenceDownload';
import WorkflowStepPanel from './WorkflowStepPanel';
import WorkflowTimeline from './WorkflowTimeline';
import { useRemediationWorkflows } from '../contexts/FeatureFlagsContext';
import { isRemediationOverdue, assignedUserIdOf } from '../utils/riskWorkflow';
import { isClosedRisk } from '../hooks/useGrcRiskWorkflow';

// A GRC control's risk workflow, in place on Security Insights: who owns the risk, its status,
// target date, progress and ticket, and the same actions as the Risks page (RiskWorkflowActions).
// With remediation workflows on, a Workflow button opens the same step panel and history as the
// Remediations page (WorkflowStepPanel + WorkflowTimeline) for the risk's remediation.
// A closed risk is shown read-only; reopening stays where it is today (Risk Register).

// The Remediations page's Workflow section for one risk's remediation: steps, then history.
const RemediationWorkflowSection = ({ risk, onChanged }) => {
    const [refreshKey, setRefreshKey] = useState(0);
    const changed = () => {
        setRefreshKey(k => k + 1);
        onChanged?.();
    };
    return (
        <div className="mt-3 pt-3 border-t border-gray-200 space-y-4" data-testid={`grc-remediation-workflow-${risk.id}`}>
            <WorkflowStepPanel
                riskId={risk.id}
                remediationId={risk.remediationId}
                remediationTitle={risk.remediationTitle}
                remediationPlan={risk.actionDescription}
                dealId={risk.dealId || null}
                businessUnitId={risk.businessUnitId || null}
                onChanged={changed}
            />
            <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Workflow history</h4>
                <WorkflowTimeline riskId={risk.id} refreshKey={refreshKey} />
            </div>
        </div>
    );
};

const RISK_STATUS = {
    identified: 'Identified',
    in_progress: 'In Progress',
    closed: 'Closed',
    accepted: 'Accepted',
    acceptancependingprimaryapproval: 'Awaiting primary approval',
    acceptancependingsecondaryapproval: 'Awaiting secondary approval',
};
export const riskStatusLabel = (status) => RISK_STATUS[String(status || '').toLowerCase()] || status || 'Unknown';

const STATUS_STYLE = {
    Identified: 'bg-red-50 text-red-700 border-red-200',
    'In Progress': 'bg-blue-50 text-blue-700 border-blue-200',
    Closed: 'bg-green-50 text-green-700 border-green-200',
    Accepted: 'bg-purple-50 text-purple-700 border-purple-200',
    'Awaiting primary approval': 'bg-amber-50 text-amber-700 border-amber-200',
    'Awaiting secondary approval': 'bg-amber-50 text-amber-700 border-amber-200',
};

// The owner the Owner column and the status line show; null when the risk has none. A risk assigned
// to an invited user who has not joined yet has no user id, but is assigned all the same.
export const ownerOf = (risk, fallbackName = null) => {
    if (risk.assignedToUserName) return risk.assignedToUserName;
    if (fallbackName) return fallbackName;
    const id = assignedUserIdOf(risk);
    if (id) return `User #${id}`;
    return risk.assignedToUserInviteId != null ? 'Invited user' : null;
};

// The controls list's workflow filter: a control matches when any of its non-closed risks does.
// mine = assigned to this user; unassigned = no owner by the same rule the Owner column uses
// (ownerOf, with the control's own owner as the fallback when it has a single risk, as that column
// shows it), so a control listed under Unassigned never shows an owner (AX-334); overdue = the
// remediations page rule.
export const matchesWorkflowFilter = (risks, filter, userId, controlOwner = null) => {
    if (filter === 'all') return true;
    const list = risks || [];
    const fallback = list.length === 1 ? controlOwner : null;
    return list.some(r => {
        if (isClosedRisk(r)) return false;
        if (filter === 'mine') return userId != null && assignedUserIdOf(r) === userId;
        if (filter === 'unassigned') return ownerOf(r, fallback) == null;
        if (filter === 'overdue') return isRemediationOverdue(r.remediationTargetDate, r.remediationStatus);
        return false;
    });
};

const formatDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

// The status line for one risk.
export const RiskWorkflowStrip = ({ risk, ownerName = null }) => {
    const status = riskStatusLabel(risk.status);
    const owner = ownerOf(risk, ownerName);
    const target = formatDate(risk.remediationTargetDate);
    const overdue = !isClosedRisk(risk) && isRemediationOverdue(risk.remediationTargetDate, risk.remediationStatus);
    return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" data-testid="grc-workflow-strip">
            <span className={`px-2 py-0.5 rounded-full border font-semibold ${STATUS_STYLE[status] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                {status}
            </span>
            {risk.remediationStatus && (
                <span className="text-gray-600">Remediation: <span className="font-medium text-gray-800">{risk.remediationStatus}</span></span>
            )}
            <span className="text-gray-600">Owner: <span className={`font-medium ${owner ? 'text-gray-800' : 'text-amber-700'}`}>{owner || 'Unassigned'}</span></span>
            <span className="text-gray-600">
                Target date: <span className={`font-medium ${overdue ? 'text-red-600' : 'text-gray-800'}`}>{target || 'Not set'}</span>
                {overdue && <span className="ml-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">Overdue</span>}
            </span>
            {/* Progress appears once a value has been saved (AX-335): a new remediation is stored at 0. */}
            {risk.progressPercentage > 0 && (
                <span className="text-gray-600">Progress: <span className="font-medium text-gray-800">{risk.progressPercentage}%</span></span>
            )}
            {risk.jiraIssueKey && (
                risk.jiraIssueUrl
                    ? <a href={risk.jiraIssueUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Jira {risk.jiraIssueKey}</a>
                    : <span className="text-gray-600">Jira {risk.jiraIssueKey}</span>
            )}
            {risk.serviceNowIncidentNumber && (
                risk.serviceNowIncidentUrl
                    ? <a href={risk.serviceNowIncidentUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">ServiceNow {risk.serviceNowIncidentNumber}</a>
                    : <span className="text-gray-600">ServiceNow {risk.serviceNowIncidentNumber}</span>
            )}
        </div>
    );
};

// Every workflow risk of a control: the status line, then one row of buttons in the Remediations
// page's order: View Risk, Workflow, the workflow actions (none on a closed risk), Download PDF.
// risks: from useGrcRiskWorkflow.risksForControl. onChanged: reload the risks and the controls.
// pending: the risk read has not finished yet (useGrcRiskWorkflow.isPending), so the section says so.
// evidenceControlId: the control whose evidence PDF Download PDF fetches (once, on the first risk).
// className: the wrapper's look; a section of a larger card passes its own (no box of its own).
const GrcControlWorkflow = ({ risks, ownerName = null, onChanged, disabledReason = null, pending = false, evidenceControlId = null, className = "bg-white border border-gray-200 rounded-lg p-3 mb-4" }) => {
    const remediationWorkflows = useRemediationWorkflows();
    const navigate = useNavigate();
    const { downloadEvidence, isDownloading } = useEvidenceDownload();
    const [openWorkflow, setOpenWorkflow] = useState(null);
    const header = (text) => <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{text}</div>;

    if ((!risks || risks.length === 0) && pending) {
        return (
            <div className={className} data-testid="grc-control-workflow-loading">
                {header('Risk workflow')}
                <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading risk workflow…
                </div>
            </div>
        );
    }
    if (!risks || risks.length === 0) return null;

    const leadingOf = (risk) => (
        <>
            <Button size="sm" variant="primary" onClick={() => navigate(`/risks?riskId=${risk.id}`)} data-testid={`grc-view-risk-${risk.id}`}>
                <Eye className="h-4 w-4 mr-2" />
                View Risk
            </Button>
            {remediationWorkflows && risk.remediationId && (
                <Button size="sm" variant={openWorkflow === risk.id ? 'secondary' : 'primary'}
                    onClick={() => setOpenWorkflow(openWorkflow === risk.id ? null : risk.id)}
                    aria-expanded={openWorkflow === risk.id} data-testid={`grc-workflow-toggle-${risk.id}`}>
                    <Workflow className="h-4 w-4 mr-2" />
                    {openWorkflow === risk.id ? 'Hide workflow' : 'Workflow'}
                </Button>
            )}
        </>
    );
    const download = evidenceControlId && (
        <Button size="sm" variant="primary" onClick={() => downloadEvidence(evidenceControlId)}
            disabled={isDownloading(evidenceControlId)} data-testid="grc-workflow-download">
            {isDownloading(evidenceControlId)
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <FileDown className="h-4 w-4 mr-2" />}
            {isDownloading(evidenceControlId) ? 'Downloading…' : 'Download PDF'}
        </Button>
    );

    return (
        <div className={className} data-testid="grc-control-workflow">
            {header(risks.length > 1 ? `${risks.length} open risks for this control` : 'Risk workflow')}
            <div className="space-y-3">
                {risks.map((risk, i) => {
                    const trailing = i === 0 ? download : null;
                    return (
                        <div key={risk.id} data-testid={`grc-workflow-risk-${risk.id}`}>
                            {risks.length > 1 && <div className="text-xs font-medium text-gray-800 mb-1">{risk.riskTitle || 'Untitled risk'}</div>}
                            <RiskWorkflowStrip risk={risk} ownerName={risks.length === 1 ? ownerName : null} />
                            {isClosedRisk(risk) ? (
                                <>
                                    <div className="text-xs text-gray-500 mt-2">This risk is closed. Open it on the Risks page to see its history.</div>
                                    <div className="flex flex-wrap gap-2 mt-2">{leadingOf(risk)}{trailing}</div>
                                </>
                            ) : (
                                <RiskWorkflowActions risk={risk} onChanged={onChanged} disabledReason={disabledReason} className="mt-2"
                                    leading={leadingOf(risk)} trailing={trailing} />
                            )}
                            {remediationWorkflows && risk.remediationId && openWorkflow === risk.id && (
                                <RemediationWorkflowSection risk={risk} onChanged={onChanged} />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default GrcControlWorkflow;
